import JoorService from '@/api/joor'

const state = {
  joorService: null,
  accountId: null,
  code: null,
  retailerId: null,
  expiresIn: null,
  accessToken: null,
  refreshToken: null,
  products: {}, // { productId: skuId[String] }
  skus: {}, // { skuId: price[Object] }
  // prices: [],
  priceType: [], // [{ id: String, name: String, currency_code: String, retail_currency_code: String }]
  currentPriceType: {},
  orders: [],
  collections: [] // [{ id: String, name: String, ... }]
}

export const getters = {
  accountId: state => state.accountId,
  code: state => state.code,
  retailerId: state => state.retailerId,
  accessToken: state => state.accessToken,
  refreshToken: state => state.refreshToken,
  products: state => state.products,
  skus: state => state.skus,
  // prices: state => state.prices,
  priceType: state => state.priceType,
  currentPriceType: state => state.currentPriceType,
  collections: state => state.collections,
  isInJoorPlatform: state => state.accessToken && window !== window.parent
}

export const actions = {
  async initJoorService ({ dispatch, commit, state, rootState }, { accountId, code, retailerId, domain, isJoorProduction }) {
    // eslint-disable-next-line no-useless-catch
    try {
      const joorService = new JoorService(domain, isJoorProduction)
      commit('SET_JOOR_SERVICE', joorService)
      commit('SET_ACCOUNT_ID', accountId)
      commit('SET_CODE', code)
      commit('SET_RETAILERID_ID', retailerId)
      await dispatch('getJoorToken')
      await dispatch('getCollections')
      await dispatch('getPriceType')
      await dispatch('getOrder')
      return true
    } catch (error) {
      throw error
    }
  },
  async getJoorToken ({ dispatch, commit, state, rootState }) {
    // eslint-disable-next-line no-useless-catch
    try {
      // eslint-disable-next-line camelcase
      const { access_token, refresh_token, expires_in } = await state.joorService.getJoorToken({ accountId: state.accountId, retailerId: state.retailerId, code: state.code })
      commit('SET_EXPIRES_IN', expires_in)
      commit('SET_ACCESS_TOKEN', access_token)
      commit('SET_REFRESH_TOKEN', refresh_token)
      return true
    } catch (error) {
      throw error
    }
  },
  async checkJoorTokenValid ({ dispatch, commit, state, rootState }) {
    // eslint-disable-next-line no-useless-catch
    try {
      if ((state.expiresIn || 0) < Date.now()) {
        await dispatch('refreshJoorToken')
      }
      return true
    } catch (error) {
      throw error
    }
  },
  async refreshJoorToken ({ dispatch, commit, state, rootState }) {
    // eslint-disable-next-line no-useless-catch
    try {
      // eslint-disable-next-line camelcase
      const { access_token, refresh_token, expires_in } = await state.joorService.refreshJoorToken(state.refreshToken)
      commit('SET_EXPIRES_IN', expires_in)
      commit('SET_ACCESS_TOKEN', access_token)
      commit('SET_REFRESH_TOKEN', refresh_token)
      return true
    } catch (error) {
      throw error
    }
  },
  async getProductProcess ({ dispatch, commit, state, rootState }, productIds) {
    // eslint-disable-next-line no-useless-catch
    try {
      let unfetchedProductIds = []
      let unfetchedSkuIds = []
      await dispatch('checkJoorTokenValid')
      unfetchedProductIds = productIds.reduce((unfetchedProductIds, currentProductId) => {
        if (!state.products[currentProductId]) {
          unfetchedProductIds.push(currentProductId)
        }
        return unfetchedProductIds
      }, [])
      if (unfetchedProductIds.length > 0) {
        const skus = await dispatch('getSkus', unfetchedProductIds)
        unfetchedSkuIds = skus.map(sku => sku.id)
      }
      if (unfetchedSkuIds.length > 0) {
        await dispatch('getPrices', unfetchedSkuIds)
      }
    } catch (error) {
      throw error
    }
  },
  async getSkus ({ dispatch, commit, state, rootState }, productIds) {
    // eslint-disable-next-line no-useless-catch
    try {
      const { data } = await state.joorService.getSkus({ accountId: state.accountId, productIds, authToken: state.accessToken })
      const products = data.reduce((products, currentSku) => {
        if (products[currentSku.product_id]) {
          products[currentSku.product_id].push(currentSku.id)
        } else {
          products[currentSku.product_id] = [currentSku.id]
        }
        return products
      }, {})
      commit('SET_PRODUCTS_WITH_SKU_ID', products)
      return data
    } catch (error) {
      throw error
    }
  },
  async getPrices ({ dispatch, commit, state, rootState }, skuIds) {
    // eslint-disable-next-line no-useless-catch
    try {
      const { data } = await state.joorService.getPrices({ accountId: state.accountId, skuIds, authToken: state.accessToken })
      const skus = data.reduce((pricesWithSkuId, currentPrice) => {
        if (pricesWithSkuId[currentPrice.sku_id]) {
          pricesWithSkuId[currentPrice.sku_id].push(currentPrice)
        } else {
          pricesWithSkuId[currentPrice.sku_id] = [currentPrice]
        }
        return pricesWithSkuId
      }, {})
      commit('SET_SKUS_WITH_PRICE', skus)
      return skus
    } catch (error) {
      throw error
    }
  },
  async getPriceType ({ dispatch, commit, state, rootState }) {
    // for orders/bulk_create need parameter: price_type_id
    // eslint-disable-next-line no-useless-catch
    try {
      await dispatch('checkJoorTokenValid')
      const { data } = await state.joorService.getPriceType({ accountId: state.accountId, authToken: state.accessToken })
      commit('SET_PRICES_TYPE', data)
      return data
    } catch (error) {
      throw error
    }
  },
  setCurrentPriceType ({ commit }, priceType) {
    commit('SET_CURRENT_PRICE_TYPE', priceType)
  },
  async getOrder ({ dispatch, commit, state, rootState }) {
    // eslint-disable-next-line no-useless-catch
    try {
      await dispatch('checkJoorTokenValid')
      const { data } = await state.joorService.getOrders({ accountId: state.accountId, retailerId: state.retailerId, authToken: state.accessToken })
      commit('SET_ORDERS', data)
      return data
    } catch (error) {
      throw error
    }
  },
  async createOrder ({ dispatch, commit, state, rootState }, collectionId) {
    try {
      await dispatch('checkJoorTokenValid')
      const { data: [order] } = await state.joorService.createOrder({
        accountId: state.accountId,
        retailerId: state.retailerId,
        authToken: state.accessToken,
        priceTypeId: state.currentPriceType.id,
        collectionId
      })
      commit('SET_ORDERS', [order])
      return order
    } catch (error) {
      if (Array.isArray(error)) {
        throw error.reduce((errorMessages, error) => {
          errorMessages += ' ' + (error.message || '')
          return errorMessages
        }, '')
      } else {
        throw error
      }
    }
  },
  async addSkuToOrder ({ dispatch, commit, state, rootState }, { order, markerInfoData }) {
    // eslint-disable-next-line no-useless-catch
    try {
      const skuIds = state.products[markerInfoData.customAttribute.joorProductId]

      const targetSkuId = skuIds ? skuIds.find(skuId => state.currentPriceType.skus[skuId]) : ''
      if (!targetSkuId) {
        throw new Error('The style doesn\'t support the selected currency')
      }

      await state.joorService.addItemToOrder({
        accountId: state.accountId,
        retailerId: state.retailerId,
        authToken: state.accessToken,
        orderId: order.id,
        skuId: targetSkuId,
        wholesaleValue: state.currentPriceType.skus[targetSkuId].wholesale_value
      })
    } catch (error) {
      throw error
    }
  },
  async addToOrderProcess ({ dispatch, commit, state, rootState }, markerInfoData) {
    // eslint-disable-next-line no-useless-catch
    try {
      await dispatch('checkJoorTokenValid')
      const collectionId = markerInfoData.customAttribute ? (markerInfoData.customAttribute.joorCollectionId || '') : ''
      const order = await dispatch('checkHasCurrentPriceTypeOrder', collectionId)
      await dispatch('addSkuToOrder', { order, markerInfoData })
    } catch (error) {
      throw error
    }
  },
  async checkHasCurrentPriceTypeOrder ({ dispatch, commit, state, rootState }, collectionId) {
    // eslint-disable-next-line no-useless-catch
    try {
      const targetOrder = state.orders.find(order => order.price_type.id === state.currentPriceType.id && order.collection.id === collectionId)
      if (targetOrder) {
        return targetOrder
      }
      const order = await dispatch('createOrder', collectionId)
      return order
    } catch (error) {
      throw error
    }
  },
  resetCurrentPriceType ({ dispatch, commit, state, rootState }) {
    commit('SET_CURRENT_PRICE_TYPE', {})
  },
  async getCollections ({ dispatch, commit, state, rootState }) {
    // eslint-disable-next-line no-useless-catch
    try {
      await dispatch('checkJoorTokenValid')
      const { data } = await state.joorService.getCollections({ accountId: state.accountId, authToken: state.accessToken })
      commit('SET_COLLECTIONS', data)
      return data
    } catch (error) {
      throw error
    }
  }
}

export const mutations = {
  SET_JOOR_SERVICE (state, joorService) {
    state.joorService = joorService
  },
  SET_ACCOUNT_ID (state, accountId) {
    state.accountId = accountId
  },
  SET_CODE (state, code) {
    state.code = code
  },
  SET_RETAILERID_ID (state, retailerId) {
    state.retailerId = retailerId
  },
  SET_EXPIRES_IN (state, expiresIn) {
    // minus one mins to get buffer to avoid expired
    state.expiresIn = Date.now() + (expiresIn - 1) * 1000
  },
  SET_ACCESS_TOKEN (state, accessToken) {
    state.accessToken = accessToken
  },
  SET_REFRESH_TOKEN (state, refreshToken) {
    state.refreshToken = refreshToken
  },
  SET_PRODUCTS_WITH_SKU_ID (state, products) {
    state.products = Object.assign({}, state.products, products)
  },
  SET_SKUS_WITH_PRICE (state, skus) {
    state.skus = Object.assign({}, state.skus, skus)
  },
  // SET_PRICES (state, prices) {
  //   state.prices = prices
  //   // state.prices.push(...prices)
  // },
  SET_PRICES_TYPE (state, priceType) {
    state.priceType = priceType
  },
  SET_CURRENT_PRICE_TYPE (state, currentPriceType) {
    state.currentPriceType = currentPriceType
  },
  SET_ORDERS (state, orders) {
    state.orders.push(...orders)
  },
  SET_COLLECTIONS (state, collections) {
    state.collections.push(...collections)
  }
}

export default {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
}
